import { DayStats } from '../types';

/**
 * Parses YYYY-MM-DD or date string into a Date object at 00:00:00 local time.
 */
export function parseDate(dateStr: string): Date {
  if (!dateStr) return new Date(2026, 8, 22, 0, 0, 0);
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // 0-indexed month
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day, 0, 0, 0);
  }
  return new Date(dateStr);
}

/**
 * Formats a Date object into YYYY-MM-DD
 */
export function formatToDateInputString(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Calculates the day count and time details relative to configured sinceDate.
 * @param sinceDateStr String in YYYY-MM-DD format (e.g. "2026-09-22")
 * @param simulatedDate Optional custom date for testing
 */
export function calculateDayStats(sinceDateStr: string = '2026-09-22', simulatedDate?: Date | null): DayStats {
  const targetDate = parseDate(sinceDateStr);
  const now = simulatedDate || new Date();
  const diffMs = now.getTime() - targetDate.getTime();
  const isAfter = diffMs >= 0;

  const absDiff = Math.abs(diffMs);
  const totalDays = Math.floor(absDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((absDiff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((absDiff / (1000 * 60)) % 60);
  const seconds = Math.floor((absDiff / 1000) % 60);

  const dayNumber = isAfter ? totalDays : -totalDays;
  const displayDayString = isAfter ? `Day ${totalDays}` : `Day -${totalDays}`;

  return {
    targetDate,
    now,
    diffMs,
    isAfter,
    dayNumber,
    totalDaysElapsed: totalDays,
    hours,
    minutes,
    seconds,
    displayDayString
  };
}

export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).format(date);
}

/**
 * Friendly readable date label with Today / Tomorrow shortcuts
 */
export function getFriendlyDateLabel(dateStr: string): string {
  if (!dateStr) return '';
  const todayStr = formatToDateInputString(new Date());
  
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = formatToDateInputString(tomorrow);

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = formatToDateInputString(yesterday);

  if (dateStr === todayStr) return 'Today';
  if (dateStr === tomorrowStr) return 'Tomorrow';
  if (dateStr === yesterdayStr) return 'Yesterday';

  const d = parseDate(dateStr);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(d);
}
